import React, { Component } from 'react';
import { isEqual, difference, get, set } from 'lodash';
import PropTypes from 'prop-types';
import 'react-table-6/react-table.css';
import ExportableTable from 'containers/tables/ExportableTable';
import { DYNAMIC_CREATIVES_DIMENSION_KEYS } from 'constants/dynamicCreatives';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import RenderingTile from 'components/molecules/RenderingTile';
import helpers from 'helpers';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import { generateFooterData, generateHeaders } from 'helpers/tables';

const DEFAULT_ACCESSORS = ['impressions', 'totalActivities', 'clicks', 'ctr', 'ecpa'];

const generateDimensionColumnFactory = ({ key, profile }) => ({
    Header: get(profile.meta, `${key}.label`, '').toUpperCase(),
    accessor: key,
    type: 'string',
    minWidth: 140,
    Aggregated: (row) => {
        const count = row.subRows.length;
        let value = `${count} Items`;
        if (count === 1) {
            value = row.subRows[0][key];
        } else if (row.isExpanded) {
            value = '';
        }
        return (
            <CellDiv
                strong={count !== 1}
                value={helpers.convertToDisplayValue(value)}
                style={{ textAlign: 'right' }}
            />
        );
    },
    PivotValue: ({ value }) => <span>{helpers.convertToDisplayValue(value)}</span>,
    Cell: row => <CellDiv value={helpers.convertToDisplayValue(row.value)} style={{ textAlign: 'right' }} />,
    Footer: (
        <CellDiv value=' ' strong />
    )
});

class DynamicCreativesPivotTable extends Component {
    constructor(props) {
        super(props);

        let columns;
        let data;
        let uniquePrimaryValues;

        if (props.hasDynamicCreativesData) {
            const table = DynamicCreativesPivotTable.createTableData(props.dynamicCreativesData, props);
            columns = table.columns;
            data = table.data;

            const pivotPrimary = get(props, 'profile.pivotPrimary');
            uniquePrimaryValues = new Set(data.map(row => get(row, [pivotPrimary]))).size;
        }

        this.state = {
            data,
            columns,
            uniquePrimaryValues,
            hasDynamicCreativesData: props.hasDynamicCreativesData,
            exportName: props.exportName,
            dynamicCreativesParams: (props.dynamicCreativesParams || {}),
        };
    }

    static getPivotByFields(props) {
        return [get(props, 'profile.pivotPrimary'), get(props, 'profile.pivotSecondary')].filter(e => e);
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps, this.props);
    }

    static createTableData(data, props) {
        const { profile } = props;

        const headers = generateHeaders({
            keys: [
                'impressions', 'spend', 'clicks', 'eCPAActivities', 'totalActivities',
                'ctr', 'cpc', 'cpa', 'ecpa', 'activityRate', 'eCPARate'
            ],
            footerData: generateFooterData({
                data,
                filteredKeys: ['dynamic_profile_name', ...DYNAMIC_CREATIVES_DIMENSION_KEYS]
            }),
            colOptions: {
                labelCode: props.entityObjectiveLabelCode
            },
            initial: DYNAMIC_CREATIVES_DIMENSION_KEYS.reduce(
                (prev, dim) => set(prev, dim, generateDimensionColumnFactory({ key: dim, profile })),
                {}
            )
        });

        const { accessors = DEFAULT_ACCESSORS } = props;

        const reportFields = DYNAMIC_CREATIVES_DIMENSION_KEYS.filter(e => get(profile.meta[e], 'showOnReport', false));
        const pivots = [profile.pivotPrimary, profile.pivotSecondary].filter(e => e);

        const columns = [
            ...pivots,
            ...difference(reportFields, pivots),
            ...accessors
        ].map(accessor => headers[accessor]);

        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextDynamicCreativesData = nextProps.dynamicCreativesData;
        const nextDynamicCreativesParams = nextProps.dynamicCreativesParams;
        const nextExportName = nextProps.exportName;
        const nextHasCreativesData = nextProps.hasDynamicCreativesData;
        let nextState = null;

        if (nextDynamicCreativesParams !== prevState.dynamicCreativesParams
            || nextDynamicCreativesData !== prevState.dynamicCreativesData
        ) {
            let columns;
            let data;
            let uniquePrimaryValues;

            if (nextHasCreativesData) {
                const table = DynamicCreativesPivotTable.createTableData(nextDynamicCreativesData, nextProps);
                columns = table.columns;
                data = table.data;

                const pivotPrimary = get(nextProps, 'profile.pivotPrimary');
                uniquePrimaryValues = new Set(data.map(row => get(row, [pivotPrimary]))).size;
            }

            nextState = {
                ...prevState,
                columns,
                data,
                exportName: nextExportName,
                hasDynamicCreativesData: nextHasCreativesData,
                dynamicCreativesParams: nextDynamicCreativesParams,
                uniquePrimaryValues
            };
        }
        return nextState;
    }

    render() {
        const { data, exportName, uniquePrimaryValues, columns, hasDynamicCreativesData } = this.state;
        const title = `DYNAMIC CREATIVE - PERFORMANCE - ${this.props.profileName}`;

        return hasDynamicCreativesData ? (
            <ContentTile padding={helpers.contentPadding('table')}>
                <ContentHeader tableData={data} exportName={exportName} type={'table'}>{title}</ContentHeader>
                <ExportableTable
                    key={1}
                    title={title}
                    exportName={exportName}
                    data={data}
                    columns={columns}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    getTfootProps= {() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    pivotBy={DynamicCreativesPivotTable.getPivotByFields(this.props)}
                    showPagination={uniquePrimaryValues > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultPageSize={RowsPerPageDefault}
                    exportRows={data.length}
                    minRows={uniquePrimaryValues < RowsPerPageDefault ? uniquePrimaryValues : RowsPerPageDefault}
                    className="-highlight"
                />
            </ContentTile>
        ) : (
            <RenderingTile height='400px' />
        );
    }
}

DynamicCreativesPivotTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    hasDynamicCreativesData: PropTypes.bool.isRequired,
    dynamicCreativesData: PropTypes.arrayOf(PropTypes.shape({
        dynamic_profile_id: PropTypes.number,
        clicks: PropTypes.number,
        ctr: PropTypes.number,
        cpc: PropTypes.number,
        impressions: PropTypes.number,
        conversions: PropTypes.number,
        cpconv: PropTypes.number,
        conversion_rate: PropTypes.number,
        spend: PropTypes.number,
    })),
    profile: PropTypes.object.isRequired,
    accessors: PropTypes.arrayOf(PropTypes.string),
    entityObjectiveLabelCode: PropTypes.string
};

export default DynamicCreativesPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/DynamicCreativesPivotTable.js